import React, { useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Row,
  Col,
  Typography,
  Descriptions,
  Image,
  Card,
  Tag,
  Divider,
  Space,
  Carousel,
} from 'antd';
import { useProductDetailData } from '../../hooks/useProductDetailData';
import { DetailSection, PriceTag, StyledCarousel, NumberCircle } from '../../styles/ProductDetailStyle';
import { numberColors } from '../../constants';
import { processImageUrls } from '../../utils';

const { Title, Paragraph } = Typography;


const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { loading, equipmentDetail, relatedProducts, error } = useProductDetailData(id);
  const [currentImage, setCurrentImage] = useState(0);
  const carouselRef = useRef();

  const getImageList = (images) => {
    if (!images) return [];
    return images.split('|').filter(Boolean);
  };

  // 规格参数
  const renderSpecifications = () => {
    const specs = {
      '型号': equipmentDetail?.model,
      '板型类别': equipmentDetail?.camberProfile,
      '板底类型': equipmentDetail?.baseType,
      '方向性': equipmentDetail?.shape,
      '硬度值': equipmentDetail?.flexIndex,
      '硬度': equipmentDetail?.flex,
      '站位': equipmentDetail?.stance,
      '固定方式': equipmentDetail?.insertPattern,
      '参考重量': equipmentDetail?.referenceWeight,
      '板芯材质': equipmentDetail?.coreMaterial,
      '适用性别': equipmentDetail?.gender,
      '颜色': equipmentDetail?.color,
      '定位': equipmentDetail?.positioning,
      '适合人群': equipmentDetail?.level,
      '风格': equipmentDetail?.style,
      '穿脱方式': equipmentDetail?.strapType,
      '底座材质': equipmentDetail?.baseMaterial,
      '可调角': equipmentDetail?.adjustable === "1" ? "是" : equipmentDetail?.adjustable === "0" ? "否" : undefined,
      '背板材质': equipmentDetail?.highbackMaterial,
      '固定盘': equipmentDetail?.mounting,
      '盘型': equipmentDetail?.discType,
      '重量': equipmentDetail?.weight,
      '长度': equipmentDetail?.length,
      '有效边刃': equipmentDetail?.effectiveEdge,
      '触雪长度': equipmentDetail?.contactLength,
      '侧切半径': equipmentDetail?.sidecutRadius,
      '板腰宽度': equipmentDetail?.waistWidth,
      // '靴子重量': equipmentDetail?.bootWeight, 因为靴子重量 数据是“ ” 或者 “0” 所以这里过滤掉
      '材质': equipmentDetail?.upperMaterial,
      '获奖情况': equipmentDetail?.award
    };
    return Object.entries(specs).filter(([_, value]) => value && value !== '0');
  };

  // 技术参数
  const renderTechnicalDetails = () => {
    const details = [
      { title: '板型', content: equipmentDetail?.boardShape },
      { title: '板芯', content: equipmentDetail?.boardCore },
      { title: '结构层', content: equipmentDetail?.construction },
      { title: '侧墙', content: equipmentDetail?.sidewall },
      { title: '板刃', content: equipmentDetail?.edge },
      { title: '板底', content: equipmentDetail?.base },
      { title: '背板技术', content: equipmentDetail?.highbackTech },
      { title: '绑带技术', content: equipmentDetail?.strapTech },
      { title: '固定盘技术', content: equipmentDetail?.discTech },
      { title: '底座技术', content: equipmentDetail?.baseTech },
      { title: '脚垫技术', content: equipmentDetail?.footbedTech },
      { title: '内胆', content: equipmentDetail?.linerTech },
      { title: '绑定', content: equipmentDetail?.closureTech },
      { title: '鞋面', content: equipmentDetail?.upperTech },
      { title: '鞋垫', content: equipmentDetail?.footbed },
      { title: '鞋底', content: equipmentDetail?.sole },
      { title: '其它技术', content: equipmentDetail?.otherTech }
    ];
    return details.filter(detail => detail.content);
  };

  // loading/error/无数据处理
  if (loading) return <div>加载中...</div>;
  if (error) return <div>加载失败: {error}</div>;
  if (!equipmentDetail) return <div>无数据</div>;

  return (
    <div>
      <Row gutter={[24, 24]}>
        <Col xs={24} md={12}>
          <DetailSection>
            <div style={{ display: 'flex', gap: '20px' }}>
              {/* 左侧同款年份侧边栏 */}
              {relatedProducts && relatedProducts.length > 0 && (
                <div style={{ 
                  width: '110px', 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: '12px',
                  maxHeight: '580px',
                  overflowY: 'auto',
                  paddingRight: '4px',
                  flexShrink: 0
                }}>
                  {relatedProducts.map(product => (
                    <div 
                      key={product.id}
                      onClick={() => navigate(`/products/${product.id}`)}
                      style={{
                        cursor: 'pointer',
                        border: product.id === id ? '2px solid #8B5CF6' : '1px solid #eee',
                        borderRadius: '8px',
                        padding: '8px',
                        textAlign: 'center',
                        position: 'relative',
                        background: '#fff',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.borderColor = '#8B5CF6';
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(139, 92, 246, 0.15)';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.borderColor = '#eee';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      <div style={{ position: 'relative', width: '100%', paddingTop: '100%' }}>
                        <img 
                          src={getImageList(product.images)[0] || '/images/placeholder.svg'} 
                          alt={product.year}
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain'
                          }}
                        />
                      </div>
                      <div style={{ 
                        fontSize: '14px', 
                        color: '#666', 
                        marginTop: '6px',
                        fontWeight: 500
                      }}>
                        {product.year}款
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* 右侧主图展示区域 */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <Image.PreviewGroup>
                  <StyledCarousel
                    ref={carouselRef}
                    beforeChange={(_, next) => setCurrentImage(next)}
                    dots={false}
                  >
                    {getImageList(equipmentDetail?.images).map((image, index) => (
                      <div key={index}>
                        <Image
                          src={image}
                          alt={`${equipmentDetail?.name}-${index + 1}`}
                          width="100%"
                          style={{ maxHeight: 480, objectFit: 'contain', cursor: 'pointer' }}
                        />
                      </div>
                    ))}
                  </StyledCarousel>
                  {/* 缩略图导航 */}
                  <div style={{ 
                    display: 'flex', 
                    gap: 8, 
                    marginTop: 16,
                    overflowX: 'auto',
                    paddingBottom: 8,
                    justifyContent: getImageList(equipmentDetail?.images).length > 5 ? 'flex-start' : 'center'
                  }}>
                    {getImageList(equipmentDetail?.images).map((image, idx) => (
                      <img
                        key={idx}
                        src={image}
                        alt={`thumb-${idx}`}
                        style={{
                          width: 60,
                          height: 60,
                          objectFit: 'cover',
                          borderRadius: 8,
                          border: currentImage === idx ? '2px solid #8B5CF6' : '2px solid #eee',
                          boxShadow: currentImage === idx ? '0 0 6px #8B5CF6' : 'none',
                          cursor: 'pointer',
                          transition: 'border 0.2s, box-shadow 0.2s',
                          flexShrink: 0
                        }}
                        onClick={() => {
                          setCurrentImage(idx);
                          carouselRef.current?.goTo(idx);
                        }}
                      />
                    ))}
                  </div>
                </Image.PreviewGroup>
              </div>
            </div>
          </DetailSection>
        </Col>

        <Col xs={24} md={12}>
          <DetailSection>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <div>
                <Title level={2}>{equipmentDetail?.name}</Title>
                <Space size={[8, 16]} wrap>
                  <Tag color="purple">{equipmentDetail?.category === "2" ? "固定器" : "滑雪板"}</Tag>
                  <Tag color="blue">{equipmentDetail?.brandName}</Tag>
                  <Tag color="cyan">{equipmentDetail?.positioning}</Tag>
                  <Tag color="gold">{equipmentDetail?.year}款</Tag>
                </Space>
              </div>
              <PriceTag>¥{equipmentDetail?.marketPrice}</PriceTag>
              {/* <div>
                <Space size={[8, 16]} wrap>
                  <Tag>浏览: {equipmentDetail?.view_num}</Tag>
                  <Tag>收藏: {equipmentDetail?.collection_num}</Tag>
                </Space>
              </div> */}
            </Space>
          </DetailSection>
        </Col>

        <Col xs={24}>
          <DetailSection>
            <Title level={3}>规格参数</Title>
            <Divider />
            <Descriptions
              bordered
              column={{ xs: 1, sm: 2, md: 3 }}
            >
              {renderSpecifications().map(([key, value]) => (
                <Descriptions.Item key={key} label={key}>
                  {value}
                </Descriptions.Item>
              ))}
            </Descriptions>
          </DetailSection>
        </Col>

        <Col xs={24}>
          <DetailSection>
            <Title level={3}>技术信息</Title>
            <Divider />
            <div style={{ textAlign: 'center' }} dangerouslySetInnerHTML={{ __html: processImageUrls(equipmentDetail?.technicalInfo) }} />
            {/* 技术参数：板型、图片、板芯、其它 */}
            {(() => {
              const details = renderTechnicalDetails();
              const result = [];
              // 找到板芯和其它技术的索引
              let startIdx = details.findIndex(d => d.title === '板芯');
              if (equipmentDetail?.category == "2") { //固定器 
                startIdx = details.findIndex(d => d.title === '背板技术');
              } else if (equipmentDetail?.category == "3") { //雪鞋
                startIdx = details.findIndex(d => d.title === '内胆');
              }
              console.log("startIdx => ", startIdx)
              const endIdx = details.findIndex(d => d.title === '其它技术');
              console.log("endIdx => ", endIdx)
              let number = 1;

              //判断类型是不是滑雪板
              if (equipmentDetail?.category == "1") {
                details.forEach((detail, index) => {
                  // 渲染板型
                  if (detail.title === '板型') {
                    result.push(
                      <div key={detail.title}>
                        <Title level={4}>{detail.title}</Title>
                        <Paragraph>
                          {detail.content.split(',').map((item, i) => (
                            <div key={i}>{item.replace(/^-/, '• ')}</div>
                          ))}
                        </Paragraph>
                        <Divider />
                      </div>
                    );
                    // 板型后插入图片
                    if (equipmentDetail?.techInfoImage) {
                      const imageUrl = equipmentDetail.techInfoImage.startsWith('http') 
                        ? equipmentDetail.techInfoImage 
                        : `https://s3.bitiful.net/breeze-snow/${equipmentDetail.techInfoImage}`;
                      result.push(
                        <div key="techInfoImage_img" style={{ textAlign: 'center', margin: '16px 0' }}>
                          <img src={imageUrl} alt="技术信息" style={{ maxWidth: '100%' }} />
                          <Divider />
                        </div>
                      );
                    }
                  } else if (
                    startIdx !== -1 &&
                    endIdx !== -1 &&
                    index >= startIdx &&
                    index < endIdx
                  ) {
                    // 对板芯到其它技术前一项编号
                    const color = numberColors[(number - 1) % numberColors.length];
                    result.push(
                      <div key={detail.title}>
                        <Title level={4} style={{ display: 'flex', alignItems: 'center' }}>
                          {detail.title}
                          <NumberCircle color={color}>{number++}</NumberCircle>
                        </Title>
                        <Paragraph>
                          {detail.content.split(',').map((item, i) => (
                            <div key={i}>{item.replace(/^-/, '• ')}</div>
                          ))}
                        </Paragraph>
                        {index < details.length - 1 && <Divider />}
                      </div>
                    );
                  } else {
                    // 其它项正常渲染
                    result.push(
                      <div key={detail.title}>
                        <Title level={4}>{detail.title}</Title>
                        <Paragraph>
                          {detail.content.split(',').map((item, i) => (
                            <div key={i}>{item.replace(/^-/, '• ')}</div>
                          ))}
                        </Paragraph>
                        {index < details.length - 1 && <Divider />}
                      </div>
                    );
                  }
                });
              } else {//除开滑雪板意外的其他，比如固定器，雪鞋
                // 先插入图片
                if (equipmentDetail?.techInfoImage) {
                  result.push(
                    <div key="techInfoImage_img" style={{ textAlign: 'center', margin: '16px 0' }}>
                      <img src={equipmentDetail.techInfoImage} alt="技术信息" style={{ maxWidth: '100%' }} />
                      <Divider />
                    </div>
                  );
                }
                details.forEach((detail, index) => {
                  if (detail.title === '其它技术') {
                    // 对其它技术不编号
                    result.push(
                      <div key={detail.title}>
                        <Title level={4}>{detail.title}</Title>
                        <Paragraph>
                          {detail.content.split(',').map((item, i) => (
                            <div key={i}>{item.replace(/^-/, '• ')}</div>
                          ))}
                        </Paragraph>
                        {index < details.length - 1 && <Divider />}
                      </div>
                    );
                  } else {
                    // 其它项正常渲染
                    const color = numberColors[(number - 1) % numberColors.length];
                    result.push(
                      <div key={detail.title}>
                        <Title level={4} style={{ display: 'flex', alignItems: 'center' }}>
                          {detail.title}
                          <NumberCircle color={color}>{number++}</NumberCircle>
                        </Title>
                        <Paragraph>
                          {detail.content.split(',').map((item, i) => (
                            <div key={i}>{item.replace(/^-/, '• ')}</div>
                          ))}
                        </Paragraph>
                        {index < details.length - 1 && <Divider />}
                      </div>
                    );                    
                  }
                });
              }
              return result;
            })()}
          </DetailSection>
        </Col>
        <Col xs={24}>
          <DetailSection>
            <div>
              <Title level={3}>产品描述</Title>
              <div style={{ textAlign: 'center' }} dangerouslySetInnerHTML={{ __html: processImageUrls(equipmentDetail?.content) }} />
            </div>
          </DetailSection>
        </Col>
        <Col xs={24}>
          <DetailSection>
            <div>
              <Title level={3}>
                尺码尺寸
                {equipmentDetail?.wideVersion === "1" && (
                  <Tag color="green" style={{ marginLeft: 12 }}>有加宽尺寸</Tag>
                )}
                {console.log('equipmentDetail?.wideVersion', equipmentDetail?.wideVersion)}
              </Title>
              {/* 加入一个判断，如果equipmentDetail.wideVersion为1就显示一个标记：有加宽尺寸 */}
              <div style={{ textAlign: 'center' }} dangerouslySetInnerHTML={{ __html: processImageUrls(equipmentDetail?.sizeChart) }} />
            </div>
          </DetailSection>
        </Col>
      </Row>
    </div>
  );
};

export default ProductDetail;